import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Grid,
  GridItem,
  Badge,
  Image,
  Spinner,
  HStack,
  VStack,
  Icon,
  Flex,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaLeaf, FaRecycle, FaHeart } from 'react-icons/fa';
import { Post } from '../types';
import { getFeaturedPosts } from '../services/mockData';
import Logo from '../components/Logo';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [featuredItems, setFeaturedItems] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFeaturedItems = async () => {
      try {
        const items = await getFeaturedPosts();
        setFeaturedItems(items);
      } catch (error) {
        console.error('Error loading featured items:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedItems();
  }, []);

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent': return 'green';
      case 'like-new': return 'blue';
      case 'good': return 'yellow';
      case 'fair': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        bgGradient="linear(135deg, brand.green.light 0%, brand.green.medium 50%, brand.green.dark 100%)"
        py={24}
        position="relative"
        overflow="hidden"
      >
        {/* Background pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.1"
          bgGradient="linear(45deg, brand.primary 25%, transparent 25%, transparent 75%, brand.primary 75%)"
          bgSize="80px 80px"
        />
        
        <Container maxW="7xl" position="relative">
          <Flex 
            direction={{ base: 'column', lg: 'row' }} 
            align="center" 
            gap={12}
          >
            {/* Left side - Text content */}
            <VStack 
              align={{ base: 'center', lg: 'start' }} 
              spacing={8} 
              flex="1"
              textAlign={{ base: 'center', lg: 'left' }}
            >
              <VStack spacing={4} align={{ base: 'center', lg: 'start' }}>
                <Heading 
                  size="3xl" 
                  color="white" 
                  fontWeight="black" 
                  lineHeight="1.1"
                  textShadow="2px 2px 4px rgba(0,0,0,0.3)"
                >
                  Reduce, Reuse,{' '}
                  <Text as="span" color="brand.primary">
                    ReDeuce
                  </Text>
                </Heading>
                <Text 
                  fontSize="xl" 
                  color="white" 
                  opacity="0.95"
                  maxW="600px"
                  fontWeight="medium"
                >
                  The sustainable marketplace for tennis equipment and gear. Buy and sell 
                  pre-owned tennis rackets, shoes, apparel, and accessories while helping protect our planet.
                </Text>
              </VStack>

              {/* Features */}
              <HStack spacing={8} flexWrap="wrap" justify={{ base: 'center', lg: 'start' }}>
                <VStack spacing={2} color="white">
                  <Icon as={FaLeaf} boxSize={6} color="brand.primary" />
                  <Text fontSize="sm" fontWeight="medium">Eco-Friendly</Text>
                </VStack>
                <VStack spacing={2} color="white">
                  <Icon as={FaRecycle} boxSize={6} color="brand.primary" />
                  <Text fontSize="sm" fontWeight="medium">Sustainable</Text>
                </VStack>
                <VStack spacing={2} color="white">
                  <Icon as={FaHeart} boxSize={6} color="brand.primary" />
                  <Text fontSize="sm" fontWeight="medium">Community</Text>
                </VStack>
              </HStack>

              {/* CTA Buttons */}
              <HStack spacing={4} flexWrap="wrap" justify={{ base: 'center', lg: 'start' }}>
                <Button
                  size="lg"
                  bg="brand.primary"
                  color="black"
                  _hover={{ 
                    bg: 'brand.primaryHover',
                    transform: 'translateY(-3px)',
                    shadow: 'xl'
                  }}
                  onClick={() => navigate('/signup')}
                  px={8}
                  py={6}
                  fontSize="lg"
                  fontWeight="bold"
                >
                  Start Selling Today
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="black"
                  color="black"
                  borderWidth="2px"
                  _hover={{ 
                    bg: 'black', 
                    color: 'brand.primary',
                    transform: 'translateY(-3px)',
                    shadow: 'xl'
                  }}
                  onClick={() => navigate('/browse')}
                  px={8}
                  py={6}
                  fontSize="lg"
                >
                  Browse Tennis Gear
                </Button>
              </HStack>
            </VStack>

            {/* Right side - Large brand mark */}
            <Box flex="1" display={{ base: 'none', lg: 'block' }}>
              <Logo
                variant="brandMark"
                size="xxl"
                maxW="500px"
                w="full"
                h="auto"
                filter="drop-shadow(0 10px 20px rgba(0,0,0,0.3))"
                _hover={{ transform: 'scale(1.05)' }}
                transition="all 0.3s"
              />
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Search Section */}
      <Box py={20} bg="brand.gray.50">
        <Container maxW="7xl">
          <VStack spacing={12}>
            <VStack spacing={6} textAlign="center">
              <Heading size="xl" color="brand.green.dark">
                Find What You're Looking For
              </Heading>
              <Text color="brand.gray.600" fontSize="lg" maxW="600px">
                Search through thousands of quality tennis equipment items from trusted sellers in your community.
              </Text>
            </VStack>
            
            {/* Search placeholder */}
            <Box 
              bg="white" 
              p={8} 
              borderRadius="xl" 
              shadow="lg" 
              border="2px solid" 
              borderColor="brand.primary"
              textAlign="center"
              w="full"
              maxW="600px"
            >
              <Text color="brand.green.medium" fontWeight="medium">
                🔍 Advanced search and filtering coming soon...
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Featured Items Grid */}
      <Box py={20}>
        <Container maxW="7xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="xl" color="brand.green.dark">
                Featured Tennis Equipment
              </Heading>
              <Text color="brand.gray.600" fontSize="lg">
                Discover amazing deals on quality tennis gear
              </Text>
            </VStack>
            
            {loading ? (
              <VStack py={16} spacing={6}>
                <Spinner size="xl" color="brand.green.medium" thickness="4px" />
                <Text color="brand.gray.600" fontSize="lg">Loading featured items...</Text>
              </VStack>
            ) : (
              <Grid 
                templateColumns={{ 
                  base: "1fr", 
                  md: "repeat(2, 1fr)", 
                  lg: "repeat(3, 1fr)", 
                  xl: "repeat(4, 1fr)" 
                }} 
                gap={8}
              >
                {featuredItems.map((item) => (
                  <GridItem key={item.id}>
                    <Box 
                      bg="white" 
                      borderRadius="xl" 
                      overflow="hidden" 
                      shadow="lg"
                      border="1px solid"
                      borderColor="brand.gray.200"
                      _hover={{ 
                        shadow: 'xl', 
                        transform: 'translateY(-8px)',
                        borderColor: 'brand.primary'
                      }}
                      transition="all 0.3s"
                      cursor="pointer"
                      onClick={() => navigate(`/post/${item.id}`)}
                    >
                      <Image
                        src={item.images[0]}
                        alt={item.title}
                        h="240px"
                        w="full"
                        objectFit="cover"
                      />
                      <Box p={6}>
                        <VStack spacing={4} align="start">
                          <Flex justify="space-between" align="center" w="full">
                            <Badge 
                              colorScheme={getConditionColor(item.condition)}
                              px={3}
                              py={1}
                              borderRadius="full"
                              fontWeight="bold"
                            >
                              {item.condition.toUpperCase()}
                            </Badge>
                            <Text 
                              fontWeight="black" 
                              fontSize="2xl" 
                              color="brand.green.dark"
                            >
                              ${item.price}
                            </Text>
                          </Flex>
                          <VStack spacing={2} align="start" w="full">
                            <Heading size="md" color="brand.green.dark" noOfLines={2}>
                              {item.title}
                            </Heading>
                            <Text color="brand.gray.600" fontSize="sm">
                              📍 {item.location}
                            </Text>
                          </VStack>
                        </VStack>
                      </Box>
                    </Box>
                  </GridItem>
                ))}
              </Grid>
            )}
          </VStack>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box py={20}>
        <Container maxW="7xl">
          <Box 
            bgGradient="linear(to-r, brand.primary, brand.primaryLight)"
            p={16} 
            borderRadius="2xl"
            textAlign="center"
            position="relative"
            overflow="hidden"
          >
            {/* Background logo watermark */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              opacity="0.1"
            >
              <Image
                src="/assets/logo/PNG/Logo_RGB.png"
                alt="ReDeuce Logo"
                h="200px"
                w="auto"
              />
            </Box>
            
            <VStack spacing={8} position="relative">
              <VStack spacing={4}>
                <Heading size="xl" color="black">
                  Ready to Start Your Sustainable Journey?
                </Heading>
                <Text fontSize="lg" color="black" opacity={0.8} maxW="600px">
                  Join thousands of tennis players and enthusiasts who are already buying and selling 
                  equipment on ReDeuce. Make a difference for your wallet and the planet.
                </Text>
              </VStack>
              
              <HStack spacing={4} flexWrap="wrap" justify="center">
                <Button
                  size="lg"
                  bg="brand.green.medium"
                  color="white"
                  _hover={{ 
                    bg: 'brand.green.dark',
                    transform: 'translateY(-3px)',
                    shadow: 'xl'
                  }}
                  onClick={() => navigate('/signup')}
                  px={8}
                  py={6}
                  fontSize="lg"
                  fontWeight="bold"
                >
                  Join ReDeuce Today
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="black"
                  color="black"
                  borderWidth="2px"
                  _hover={{ 
                    bg: 'black', 
                    color: 'brand.primary',
                    transform: 'translateY(-3px)',
                    shadow: 'xl'
                  }}
                  onClick={() => navigate('/browse')}
                  px={8}
                  py={6}
                  fontSize="lg"
                >
                  Browse Tennis Gear
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 